
import { useState, useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CampLocation } from "@/types/campLocation";
import { UserInvitation } from "@/types/userInvitation";
import { useFirestoreError } from "@/hooks/useFirestoreError";
import { doc, getFirestore, updateDoc, getDoc } from "firebase/firestore";
import { toast } from "sonner";
import { formSchema, LocationFormData } from "@/components/admin/locations/schemas/locationFormSchema";
import { useLocationFormInit } from "@/components/admin/locations/hooks/useLocationFormInit";
import { createLocationVersion } from "@/services/locationVersionCreateService";
import { getCachedLatestLocationVersion } from "@/services/locationVersion/locationVersionCacheService";
import { updateCampLocation } from "@/services/campLocationUpdateService";
import { Organization } from "@/types/organization";
import { isLocationEditLiveSource } from "@/config/locationEditFlag";

export const useWelcomeLocationEdit = (
  location: CampLocation | null,
  invitation: UserInvitation | null
) => {
  const { handleFirestoreError } = useFirestoreError();
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [changeDescription, setChangeDescription] = useState("");
  const [manualSave, setManualSave] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const [organizationData, setOrganizationData] = useState<Organization | null>(null);
  
  console.log("useWelcomeLocationEdit: Location:", location?.id, "Invitation:", invitation?.id);
  
  // Track if we have a valid location for initialization
  const hasValidLocation = Boolean(location && location.id);
  console.log("useWelcomeLocationEdit: hasValidLocation:", hasValidLocation);
  
  // Use ref to track location ID changes to avoid re-renders
  const locationIdRef = useRef<string | null>(location?.id || null);
  
  // Initialize form with the location data or empty defaults if no location yet
  const formData = useLocationFormInit(location);
  
  // Fetch organization data if available from the location
  useEffect(() => {
    const fetchOrganizationData = async () => {
      if (location && location.providedByOrg) {
        try {
          const db = getFirestore();
          const orgId = typeof location.providedByOrg === 'string' 
            ? location.providedByOrg 
            : location.providedByOrg.id;
          
          if (!orgId) return;
          
          const orgRef = doc(db, "campOrganizations", orgId);
          const orgDoc = await getDoc(orgRef);
          
          if (orgDoc.exists()) {
            const orgData = orgDoc.data() as Organization;
            setOrganizationData(orgData);
          }
        } catch (error) {
          console.error("Error fetching organization data:", error);
        }
      }
    };
    
    if (hasValidLocation) {
      fetchOrganizationData();
    }
  }, [location, hasValidLocation]);
  
  // If location ID changed, update the ref
  useEffect(() => {
    if (location?.id !== locationIdRef.current) {
      console.log(`useWelcomeLocationEdit: Location ID changed from ${locationIdRef.current} to ${location?.id}`);
      locationIdRef.current = location?.id || null;
    }
  }, [location?.id]);
  
  // Update isDirty when formData changes
  useEffect(() => {
    if (formData) {
      console.log("useWelcomeLocationEdit: Updating isDirty from formData:", formData.isDirty);
      setIsDirty(formData.isDirty);
    }
  }, [formData?.isDirty]);
  
  // Create a default form instance as fallback
  const defaultForm = useForm<LocationFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { 
      name: "", 
      visibility: "v_draft" as const,
      address: {
        address1: "", 
        address2: "", 
        city: "",
        state: "", 
        zip: "", 
        country: "USA",
        latLng: "",
        webUrl: "",
        localityLabel: ""
      },
      doesWaitlist: null,
      doesScholarships: null,
      campExperiences: [{
        programName: "Default",
        topActivities: "",
        outdoorTimeScale: 50,
        structuredTimeScale: 50,
        physicalityScale: 50,
        themeMainKey: "",
        themeOtherKeys: []
      }]
    }
  });
  
  // Use the appropriate form instance
  const form = formData?.form || defaultForm;
  
  // Log form value once initialized
  useEffect(() => {
    if (hasValidLocation && location) {
      console.log("useWelcomeLocationEdit: Form initialized with location:", location.id);
    }
  }, [hasValidLocation, location]);
  
  // Function to fetch the latest draft version of the location
  const fetchLatestDraft = async (locationId: string) => {
    if (!locationId) return null;

    // KB-3308: live-source mode edits the live doc; there is no draft version to fetch or merge.
    if (isLocationEditLiveSource()) return null;

    try {
      console.log("Fetching latest draft for location:", locationId);
      const db = getFirestore();
      const latestDraft = await getCachedLatestLocationVersion(db, locationId, true);
      console.log("Latest draft fetched:", latestDraft?.versionId);
      return latestDraft;
    } catch (error) {
      console.error("Error fetching latest draft:", error);
      return null;
    }
  };
  
  const onSaveLocation = useCallback(
    async (
      updatedLocation: CampLocation, 
      changeDescription?: string
    ): Promise<void> => {
      if (!location || !location.id) {
        toast.error("No location to save");
        return Promise.resolve();
      }
      
      try {
        console.log("Saving location from welcome page:", location.id);
        setIsSaving(true);
        
        const db = getFirestore();
        // Fix: Use the invitation ID directly instead of accessing non-existent user property
        const userId = invitation?.id || invitation?.token || "token-user";
        
        console.log("Using user ID for save:", userId);
        
        // IMPORTANT CHANGE: Use forceUpdateLatestDraft=true to update the existing draft
        // instead of creating a new version for token users
        await updateCampLocation(
          db,
          location.id,
          updatedLocation,
          userId,
          changeDescription || 'Updated from welcome page',
          true, // Force update to latest draft instead of creating a new version
          false
        );
        
        console.log(`Updated latest draft for location ${location.id}`);
        
        // Reset the dirty state after successful save
        setIsDirty(false);
        if (formData) {
          formData.setIsDirty(false);
        }
        
        // Update last saved time
        setLastSavedTime(new Date());
        
        // Dispatch version change event to refresh other components
        window.dispatchEvent(new CustomEvent("locationVersionChanged", { 
          detail: { locationId: location.id } 
        }));
        
        // Fetch the latest draft after saving to make sure we're working with the updated data
        const latestDraft = await fetchLatestDraft(location.id);
        if (latestDraft) {
          console.log("Retrieved latest draft after save:", latestDraft.versionId);
          
          // Dispatch an event to notify WelcomeLocationEditingState to refresh with latest draft
          window.dispatchEvent(new CustomEvent("refreshWelcomeLatestDraft", { 
            detail: { 
              locationId: location.id,
              latestDraft: latestDraft
            }
          }));
        }
        
        toast.success("Location updated successfully");
        return Promise.resolve();
      } catch (error) {
        console.error("Error saving location:", error);
        handleFirestoreError(error);
        
        // Detailed error logging for debugging
        console.log("Save Error Details:", {
          locationId: location.id,
          invitationId: invitation?.id,
          invitationToken: invitation?.token,
          organizationId: invitation?.organization?.id,
          errorMessage: error instanceof Error ? error.message : "Unknown error",
          errorStack: error instanceof Error ? error.stack : undefined
        });
        
        return Promise.reject(error);
      } finally {
        setIsSaving(false);
      }
    },
    [location, invitation, handleFirestoreError, formData]
  );
  
  // Create a handler for manual save that works with both form states
  const handleManualSave = useCallback((formInstance: any) => {
    if (!location || !location.id) {
      console.log("No location available to save");
      toast.error("No location available to save");
      return Promise.resolve();
    }
    
    console.log("handleManualSave: Initiating manual save for location:", location.id);
    setManualSave(true);
    
    // Convert form data to location object and save
    const formData = formInstance.getValues();
    console.log("Form data:", formData);
    
    // Create updated location object from form data
    const updatedLocation: CampLocation = {
      ...location,
      name: formData.name,
      urlMain: formData.urlMain,
      searchResultExcerpt: formData.searchResultExcerpt,
      descLocation: formData.descLocation,
      descTimingAndBooking: formData.descTimingAndBooking,
      address: formData.address,
      doesWaitlist: formData.doesWaitlist,
      doesScholarships: formData.doesScholarships,
      scholarshipNotes: formData.scholarshipNotes,
      religiousAffiliationKeys: formData.religiousAffiliationKeys,
      religiousAffiliationNotes: formData.religiousAffiliationNotes,
      allergyKeys: formData.allergyKeys,
      allergyNotes: formData.allergyNotes,
      supportForKeys: formData.supportForKeys,
      supportForNotes: formData.supportForNotes,
      campExperiences: formData.campExperiences,
      regularOfferIncrementKey: formData.regularOfferIncrementKey,
      incrementPrice: formData.incrementPrice,
      priceNote: formData.priceNote,
      singleDayOption: formData.singleDayOption,
      singleDayPrice: formData.singleDayPrice,
    };
    
    // Save the location
    return onSaveLocation(updatedLocation, changeDescription)
      .then(() => {
        // Update form status after successful save
        setManualSave(false);
      })
      .catch((error) => {
        console.error("Manual save error:", error);
        setManualSave(false);
        throw error; // Re-throw to allow parent components to handle
      });
  }, [location, onSaveLocation, changeDescription]);
    
  // Return merged state that is consistent whether we have a location or not
  return {
    form,
    isSaving: formData?.isSaving || isSaving,
    changeDescription,
    setChangeDescription,
    isDirty: formData?.isDirty || isDirty,
    setIsDirty,
    handleManualSave,
    onSaveLocation,
    lastSavedTime,
    fetchLatestDraft
  };
};
